import React, { useEffect } from 'react';
import { useToast, useProbabilityToast } from '@/components/ui/use-toast';
import {
	ToastProvider,
	ToastViewport,
	Toast,
	ToastTitle,
	ToastDescription,
	ToastClose,
} from '@/components/ui/toast';
import { ACHIEVEMENT_COPY } from '@/constants/achievements.constants';
import { getAchievementMetadata, resolveAchievementMessage } from '@sportspredict/shared';
import { cn, renderMessage } from '@/utils';
import { ArrowRight, Check, CheckCircle2, X } from 'lucide-react';

const SNACKBAR_TOAST_VARIANTS = new Set([
	'toast.probability.all-markets-traded',
	'toast.friend-request',
]);

const ADMIN_SNACKBAR_VARIANTS = new Set(['admin.snackbar', 'admin.snackbar.error']);

export function Toaster() {
	const { toasts, dismiss } = useToast();
	const { toast: probToast, dismiss: dismissProb } = useProbabilityToast();
	const openToasts = toasts.filter((t) => t.open);
	return (
		<ToastProvider swipeDirection="right" isExistOpenToasts={openToasts.length === 0}>
			{openToasts.map((t) => (
				<ToastItem key={t?.id} toast={t} dismiss={dismiss} />
			))}
			<ToastViewport />
			{probToast && <ProbabilityToastBanner toast={probToast} onDismiss={dismissProb} />}
		</ToastProvider>
	);
}

function ToastItem({ toast, dismiss }) {
	useEffect(() => {
		const timer = setTimeout(() => dismiss(toast.id), toast.duration ?? 3000);
		return () => clearTimeout(timer);
	}, [toast.id, toast.duration, dismiss]);

	if (ADMIN_SNACKBAR_VARIANTS.has(toast.variant)) {
		const isError = toast.variant === 'admin.snackbar.error';
		return (
			<Toast
				{...toast}
				onOpenChange={(open) => {
					if (!open) dismiss(toast.id);
				}}
			>
				<div className="flex min-w-0 flex-1 items-center gap-2">
					<div className="h-6 w-0.5 shrink-0 rounded bg-[#506296]" />
					<p
						className={cn(
							"min-w-0 break-words font-['DM_Sans'] text-sm font-normal leading-snug",
							isError ? 'text-[#da3030]' : 'text-white'
						)}
					>
						{toast.description || toast.title}
					</p>
				</div>
				<div className="flex size-6 shrink-0 items-center justify-center">
					{isError ? (
						<X className="h-5 w-5 text-[#da3030]" strokeWidth={2} />
					) : (
						<Check className="h-5 w-5 text-white" strokeWidth={2} />
					)}
				</div>
			</Toast>
		);
	}

	if (SNACKBAR_TOAST_VARIANTS.has(toast.variant)) {
		return (
			<Toast
				{...toast}
				onOpenChange={(open) => {
					if (!open) dismiss(toast.id);
				}}
			>
				<div className="flex-1 min-w-0 font-['DM_Sans'] font-medium text-[14px] leading-[20px] tracking-[-0.364px] text-white">
					{toast.description || toast.title}
				</div>
				<div className="shrink-0 relative overflow-hidden w-6 h-6 flex items-center justify-center">
					<CheckCircle2 className="w-6 h-6 text-white" strokeWidth={2} />
				</div>
			</Toast>
		);
	}

	return (
		<Toast
			{...toast}
			onOpenChange={(open) => {
				if (!open) dismiss(toast.id);
			}}
		>
			<div className="grid gap-1">
				{toast.title && <ToastTitle>{toast.title}</ToastTitle>}
				{toast.description && <ToastDescription>{toast.description}</ToastDescription>}
			</div>
			{toast.action}
			<ToastClose aria-label="Close" onClick={() => dismiss(toast.id)} />
		</Toast>
	);
}

function ProbabilityToastBanner({ toast, onDismiss }) {
	const copy = ACHIEVEMENT_COPY[toast.achievementType];
	const description =
		resolveAchievementMessage(getAchievementMetadata(toast.achievementType), toast.botName) ||
		copy?.message;

	useEffect(() => {
		toast.onShow?.();
		const timer = setTimeout(onDismiss, 5000);
		return () => clearTimeout(timer);
	}, [toast.achievementType, onDismiss]);

	if (!copy) return null;
	return (
		<div
			style={{
				pointerEvents: 'auto',
			}}
			className="fixed z-[9999] cursor-pointer font-['DM_Sans']
				lg:top-[96px] lg:left-[24px] lg:max-w-[477px] lg:w-auto
				top-[60px] left-4 right-4"
			onClick={(e) => {
				toast.onClick?.(e);
				onDismiss();
			}}
		>
			<div
				className="flex items-center gap-1 rounded-2xl px-3 pr-2 py-2"
				style={{
					background: 'linear-gradient(137.04deg, #FFFFFF 50.55%, #DCE3F9 138.73%)',
					border: '0.5px solid #ACC0F6',
					boxShadow: '0px 16px 32px -8px rgba(12,12,13,0.4)',
				}}
			>
				{/* Left accent bar */}
				<div className="shrink-0 w-[2px] h-8 rounded-[2px] bg-[#2667FF]" />

				{/* Badge image */}
				<img
					src={copy.icon}
					alt={copy.title}
					className="shrink-0 w-10 h-10 rounded-lg object-cover ml-1"
				/>

				{/* Text */}
				<div className="flex-1 min-w-0 mx-2">
					<p className="text-[12px] font-semibold leading-[18px] tracking-[0.48px] text-[#0A0A0A] truncate uppercase">
						{copy.title}
					</p>
					<p className="text-[12px] font-medium leading-[14px] tracking-[-0.12px] text-[#474747] truncate">
						{renderMessage(description)}
					</p>
				</div>

				{/* Arrow + close */}
				<div className="flex items-center gap-1 shrink-0">
					<ArrowRight className="w-4 h-4 text-[#474747]" />
				</div>
			</div>
		</div>
	);
}
